import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./HomePage.css";

gsap.registerPlugin(ScrollTrigger);

/* ══════════════════════════════════════════════════════════
   DATA
══════════════════════════════════════════════════════════ */
const DESTINATIONS = [
    { flag: "🇺🇸", name: "USA", count: "450+", img: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?q=80&w=900&auto=format&fit=crop" },
    { flag: "🇬🇧", name: "United Kingdom", count: "280+", img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=900&auto=format&fit=crop" },
    { flag: "🇦🇺", name: "Australia", count: "190+", img: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?q=80&w=900&auto=format&fit=crop" },
    { flag: "🇨🇦", name: "Canada", count: "120+", img: "https://images.unsplash.com/photo-1517935706615-2717063c2225?q=80&w=900&auto=format&fit=crop" },
    { flag: "🇩🇪", name: "Germany", count: "60+", img: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?q=80&w=900&auto=format&fit=crop" },
    { flag: "🇳🇿", name: "New Zealand", count: "45+", img: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=900&auto=format&fit=crop" },
];

const SERVICES = [
    { icon: "🎯", title: "Career Counselling", desc: "Personalised roadmap built around your strengths, goals, and the global job market." },
    { icon: "🏛️", title: "University Selection", desc: "Shortlist from 50+ partner universities — ranked, funded, and matched to your profile." },
    { icon: "📋", title: "Visa Assistance", desc: "End-to-end documentation, financial proofs, and mock interviews. 100% success in 2025." },
    { icon: "✍️", title: "Application & SOPs", desc: "Compelling statements of purpose, LORs, and CVs crafted with your unique story." },
    { icon: "📊", title: "Test Preparation", desc: "IELTS, TOEFL, GRE, GMAT strategy — right score targets with the right coaching." },
    { icon: "✈️", title: "Pre-Departure Support", desc: "Accommodation, banking, insurance, and orientation — land ready, not lost." },
];

const PROCESS = [
    { step: "01", title: "Free Profile Evaluation", desc: "Book a no-cost session. We assess your academics, budget, and goals." },
    { step: "02", title: "Personalised Roadmap", desc: "Receive a curated university shortlist and step-by-step action plan." },
    { step: "03", title: "Applications & Visa", desc: "We craft your documents and manage every visa detail meticulously." },
    { step: "04", title: "Fly with Confidence", desc: "Pre-departure orientation so you land knowing exactly what to do." },
];

const TESTIMONIALS = [
    { initials: "AK", name: "Arjun Kiran", dest: "Uni. of Melbourne, Australia", color: "#C0392B", stars: 5, quote: "From SOP to visa approval — zero stress. I got my offer letter in just 3 weeks. ISTP made everything possible." },
    { initials: "PS", name: "Priya Sharma", dest: "Uni. of Edinburgh, UK", color: "#1A3A5C", stars: 5, quote: "No false promises. They told me exactly which universities matched my profile and delivered exactly that." },
    { initials: "RM", name: "Rahul Mehta", dest: "Northeastern Uni., USA", color: "#922B21", stars: 5, quote: "The counsellors know every university inside out. My visa was approved on the very first attempt." },
    { initials: "SD", name: "Sneha Devi", dest: "Uni. of Toronto, Canada", color: "#1A3A5C", stars: 5, quote: "I was confused and scared about the whole process. ISTP gave me a clear plan and supported me throughout." },
    { initials: "VR", name: "Vikram Reddy", dest: "Uni. of Auckland, New Zealand", color: "#C0392B", stars: 5, quote: "Exceptional support — they handled everything from my IELTS prep to the final visa stamp. Highly recommend." },
    { initials: "NK", name: "Neha Kulkarni", dest: "TU Munich, Germany", color: "#922B21", stars: 5, quote: "Got a scholarship I didn't even know I was eligible for. ISTP's knowledge of funding is unmatched." },
    { initials: "SA", name: "Suresh Anand", dest: "Uni. of Sydney, Australia", color: "#1A3A5C", stars: 5, quote: "The SOP they helped me write was truly exceptional. My dream university accepted me in round one." },
    { initials: "MP", name: "Meera Pillai", dest: "Queen Mary Uni., London, UK", color: "#C0392B", stars: 5, quote: "ISTP treated my application like it was their own. That personal touch made all the difference." },
    { initials: "KT", name: "Karan Tiwari", dest: "York Uni., Canada", color: "#922B21", stars: 5, quote: "Fast, transparent, and genuinely caring. I had my study permit approved in under 6 weeks." },
    { initials: "DL", name: "Divya Lakshmi", dest: "Drexel Uni., USA", color: "#1A3A5C", stars: 5, quote: "From day one they made me feel heard. Best decision I made was walking into the ISTP office." },
];

/* ══════════════════════════════════════════════════════════
   ANIMATED COUNTER
══════════════════════════════════════════════════════════ */
function AnimatedCounter({ target, suffix = "" }) {
    const [val, setVal] = useState(0);
    const ref = useRef(null);
    const triggered = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const st = ScrollTrigger.create({
            trigger: el,
            start: "top 88%",
            once: true,
            onEnter: () => {
                if (triggered.current) return;
                triggered.current = true;
                gsap.to({ v: 0 }, {
                    v: target, duration: 1.9, ease: "power3.out",
                    onUpdate: function () { setVal(Math.round(this.targets()[0].v)); },
                });
            },
        });
        return () => st.kill();
    }, [target]);

    return <span ref={ref}>{val}{suffix}</span>;
}

/* ══════════════════════════════════════════════════════════
   LIVE COUNTDOWN
══════════════════════════════════════════════════════════ */
function Countdown() {
    const calc = () => {
        const now = new Date();
        const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
        const t = new Date(ist); t.setHours(19, 0, 0, 0);
        if (ist >= t) t.setDate(t.getDate() + 1);
        const d = Math.max(0, Math.floor((t - ist) / 1000));
        return { h: Math.floor(d / 3600), m: Math.floor((d % 3600) / 60), s: d % 60 };
    };
    const [time, setTime] = useState(calc());
    useEffect(() => { const id = setInterval(() => setTime(calc()), 1000); return () => clearInterval(id); }, []);
    const pad = (n) => String(n).padStart(2, "0");

    return (
        <div className="hp-countdown">
            {[{ v: pad(time.h), l: "hrs" }, { v: pad(time.m), l: "min" }, { v: pad(time.s), l: "sec" }].map((u, i) => (
                <React.Fragment key={i}>
                    {i > 0 && <span className="hp-cd-sep">:</span>}
                    <div className="hp-cd-unit">
                        <span className="hp-cd-val">{u.v}</span>
                        <span className="hp-cd-lbl">{u.l}</span>
                    </div>
                </React.Fragment>
            ))}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════
   TESTIMONIAL CAROUSEL
══════════════════════════════════════════════════════════ */
function TestimonialCarousel() {
    const [active, setActive] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const cardRef = useRef(null);
    const autoRef = useRef(null);
    const total = TESTIMONIALS.length;

    const goTo = useCallback((idx) => {
        if (isAnimating) return;
        setIsAnimating(true);
        gsap.to(cardRef.current, {
            opacity: 0, y: 16, duration: 0.25, ease: "power2.in",
            onComplete: () => {
                setActive(idx);
                gsap.fromTo(cardRef.current,
                    { opacity: 0, y: -16 },
                    {
                        opacity: 1, y: 0, duration: 0.35, ease: "power2.out",
                        onComplete: () => setIsAnimating(false)
                    }
                );
            },
        });
    }, [isAnimating]);

    const next = useCallback(() => goTo((active + 1) % total), [active, total, goTo]);
    const prev = useCallback(() => goTo((active - 1 + total) % total), [active, total, goTo]);

    useEffect(() => {
        autoRef.current = setInterval(next, 5000);
        return () => clearInterval(autoRef.current);
    }, [next]);

    const resetAuto = () => {
        clearInterval(autoRef.current);
        autoRef.current = setInterval(next, 5000);
    };

    const t = TESTIMONIALS[active];

    return (
        <div className="hp-testi-carousel">
            <div className="hp-testi-card-wrap" ref={cardRef}>
                <div className="hp-testi-card">
                    <div className="hp-testi-quote-icon">"</div>
                    <p className="hp-testi-quote">{t.quote}</p>
                    <div className="hp-testi-author">
                        <div className="hp-testi-av" style={{ background: t.color }}>{t.initials}</div>
                        <div>
                            <div className="hp-testi-name">{t.name}</div>
                            <div className="hp-testi-dest">{t.dest}</div>
                        </div>
                        <div className="hp-testi-stars">{"★".repeat(t.stars)}</div>
                    </div>
                </div>
            </div>

            <div className="hp-testi-controls">
                <button className="hp-testi-btn" onClick={() => { prev(); resetAuto(); }} aria-label="Previous">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <div className="hp-testi-dots">
                    {TESTIMONIALS.map((_, i) => (
                        <button
                            key={i}
                            className={`hp-testi-dot${i === active ? " hp-testi-dot--active" : ""}`}
                            onClick={() => { goTo(i); resetAuto(); }}
                            aria-label={`Go to testimonial ${i + 1}`}
                        />
                    ))}
                </div>
                <button className="hp-testi-btn" onClick={() => { next(); resetAuto(); }} aria-label="Next">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
            </div>

            <div className="hp-testi-thumbs">
                {TESTIMONIALS.map((tt, i) => (
                    <button
                        key={i}
                        className={`hp-testi-thumb${i === active ? " hp-testi-thumb--active" : ""}`}
                        onClick={() => { goTo(i); resetAuto(); }}
                    >
                        <span className="hp-thumb-av" style={{ background: tt.color }}>{tt.initials}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function HomePage() {
    /* ── Refs ── */
    const rootRef = useRef(null);
    const heroRef = useRef(null);
    const heroSubRef = useRef(null);
    const heroBtnRef = useRef(null);
    const morphShapeRef = useRef(null);
    const aboutRef = useRef(null);
    const aboutMediaRef = useRef(null);
    const aboutBodyRef = useRef(null);
    const aboutImgRef = useRef(null);
    const expBadgeRef = useRef(null);
    const statsRef = useRef(null);
    const servicesRef = useRef(null);
    const svcTrackRef = useRef(null);
    const svcProgressRef = useRef(null);
    const processRef = useRef(null);
    const procLineRef = useRef(null);
    const destRef = useRef(null);
    const destImgRef = useRef(null);
    const liveRef = useRef(null);
    const testiRef = useRef(null);
    const ctaRef = useRef(null);
    const ctaBgTextRef = useRef(null);
    const svcCardsRef = useRef([]);
    const procStepsRef = useRef([]);

    const [activeDestIdx, setActiveDestIdx] = useState(0);
    const destShowcaseRef = useRef(null);

    /* ══ All animations wrapped in gsap.context for clean scoped cleanup ══ */
    useEffect(() => {
        const ctx = gsap.context(() => {

            /* ─── HERO ENTRANCE (masked line reveals) ─── */
            const heroTl = gsap.timeline({ delay: 0.15, defaults: { ease: "power4.out" } });
            heroTl
                .to(".hp-hero-h1 .line-inner", { y: 0, duration: 1.15, stagger: 0.11 })
                .to(heroSubRef.current, { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }, "-=0.55")
                .to(heroBtnRef.current, { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" }, "-=0.5")
                .fromTo(".hp-hero-card",
                    { opacity: 0, y: 50, scale: 0.96 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.95, ease: "power3.out" }, "-=0.7")
                .fromTo(".hp-float-pill",
                    { opacity: 0, y: 12, scale: 0.85 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.55, stagger: 0.14, ease: "back.out(1.4)" }, "-=0.4")
                .fromTo(morphShapeRef.current,
                    { opacity: 0, scale: 0.5 },
                    { opacity: 1, scale: 1, duration: 1.1, ease: "elastic.out(1, 0.55)" }, "-=0.9");

            /* ─── MORPH SHAPE TRAVELS WITH SCROLL ─── */
            gsap.to(morphShapeRef.current, {
                y: "130vh",       
                x: "-28vw",    
                scale: 1.35,
                borderRadius: "50% 30% 70% 40% / 40% 50% 60% 50%",
                ease: "none",
                scrollTrigger: {
                    trigger: heroRef.current,
                    start: "top top",
                    end: "bottom top",
                    scrub: 1.4,
                },
            });

            ScrollTrigger.create({
                trigger: aboutRef.current,
                start: "top 75%",
                once: true,
                onEnter: () =>
                    gsap.to(morphShapeRef.current, { opacity: 0, scale: 0.6, duration: 0.6, ease: "power2.in" }),
            });

            /* ─── STATS — pin briefly while counters run ─── */
            gsap.fromTo(statsRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1, y: 0, duration: 0.85, ease: "power3.out",
                    scrollTrigger: { trigger: statsRef.current, start: "top 82%", once: true }
                }
            );

            /* ─── ABOUT — parallax image, tilt-in experience badge ─── */
            gsap.fromTo(aboutMediaRef.current,
                { opacity: 0, x: -50 },
                {
                    opacity: 1, x: 0, duration: 0.95, ease: "power3.out",
                    scrollTrigger: { trigger: aboutRef.current, start: "top 78%", once: true }
                }
            );
            gsap.fromTo(aboutBodyRef.current,
                { opacity: 0, x: 50 },
                {
                    opacity: 1, x: 0, duration: 0.95, ease: "power3.out",
                    scrollTrigger: { trigger: aboutRef.current, start: "top 78%", once: true }
                }
            );
            if (aboutImgRef.current) {
                gsap.fromTo(aboutImgRef.current,
                    { yPercent: -7 },
                    {
                        yPercent: 7, ease: "none",
                        scrollTrigger: {
                            trigger: aboutRef.current,
                            start: "top bottom",
                            end: "bottom top",
                            scrub: true,
                        }
                    }
                );
            }
            if (expBadgeRef.current) {
                gsap.fromTo(expBadgeRef.current,
                    { opacity: 0, scale: 0.75, rotation: -6 },
                    {
                        opacity: 1, scale: 1, rotation: 0, duration: 0.95, ease: "back.out(1.5)", delay: 0.25,
                        scrollTrigger: { trigger: aboutRef.current, start: "top 72%", once: true }
                    }
                );
            }

            /* ─── SERVICES — HORIZONTAL SCROLL (desktop) ─── */
            const mm = gsap.matchMedia();
            mm.add("(min-width: 900px)", () => {
                if (!servicesRef.current || !svcTrackRef.current) return;

                const track = svcTrackRef.current;
                const getScrollAmount = () => {
                    const overflow = track.scrollWidth - window.innerWidth + (window.innerWidth * 0.06);
                    return overflow > 0 ? overflow : 0;
                };


                gsap.to(track, {
                    x: () => -getScrollAmount(),
                    ease: "none",
                    scrollTrigger: {
                        trigger: servicesRef.current,
                        start: "top top",
                        end: () => `+=${getScrollAmount()}`,
                        pin: true,
                        pinSpacing: true,        // ← was false — this adds the spacer automatically
                        anticipatePin: 1,
                        scrub: 1,
                        invalidateOnRefresh: true,
                        onUpdate: (self) => {
                            if (svcProgressRef.current) {
                                svcProgressRef.current.style.transform = `scaleX(${self.progress})`;
                            }
                        },
                    },
                });

                // Stagger in the service cards on first approach
                gsap.fromTo(".hp-svc-card",
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1, y: 0, duration: 0.7, stagger: 0.08, ease: "power3.out",
                        scrollTrigger: { trigger: servicesRef.current, start: "top 70%", once: true }
                    }
                );

                // Side copy reveal
                gsap.fromTo(".hp-svc-side > *",
                    { opacity: 0, y: 24 },
                    {
                        opacity: 1, y: 0, duration: 0.85, stagger: 0.1, ease: "power3.out",
                        scrollTrigger: { trigger: servicesRef.current, start: "top 70%", once: true }
                    }
                );
            });

            mm.add("(max-width: 899px)", () => {
                // Mobile: simple stagger, no pin
                gsap.fromTo(".hp-svc-side > *",
                    { opacity: 0, y: 20 },
                    {
                        opacity: 1, y: 0, duration: 0.7, stagger: 0.1, ease: "power3.out",
                        scrollTrigger: { trigger: servicesRef.current, start: "top 78%", once: true }
                    }
                );
                gsap.fromTo(".hp-svc-card",
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1, y: 0, duration: 0.55, stagger: 0.08, ease: "power2.out",
                        scrollTrigger: { trigger: servicesRef.current, start: "top 78%", once: true }
                    }
                );
            });

            /* ─── PROCESS — the line draws itself as you scroll ─── */
            gsap.fromTo(processRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 0.85, ease: "power3.out",
                    scrollTrigger: { trigger: processRef.current, start: "top 80%", once: true }
                }
            );

            if (procLineRef.current) {
                const path = procLineRef.current.querySelector("path");
                if (path) {
                    const len = path.getTotalLength();
                    gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
                    gsap.to(path, {
                        strokeDashoffset: 0,
                        ease: "none",
                        scrollTrigger: {
                            trigger: processRef.current,
                            start: "top 65%",
                            end: "bottom 70%",
                            scrub: 1,
                        },
                    });
                }
            }

            // Process steps reveal one by one
            procStepsRef.current.forEach((step, i) => {
                if (!step) return;
                gsap.fromTo(step,
                    { opacity: 0, y: 32 },
                    {
                        opacity: 1, y: 0, duration: 0.7, delay: i * 0.12, ease: "power3.out",
                        scrollTrigger: { trigger: processRef.current, start: "top 70%", once: true }
                    }
                );
            });

            /* ─── DESTINATIONS — reveal + image parallax ─── */
            gsap.fromTo(destRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 0.85, ease: "power3.out",
                    scrollTrigger: { trigger: destRef.current, start: "top 80%", once: true }
                }
            );
            if (destImgRef.current) {
                gsap.fromTo(destImgRef.current.querySelector("img"),
                    { scale: 1.08, yPercent: -4 },
                    {
                        scale: 1, yPercent: 4, ease: "none",
                        scrollTrigger: {
                            trigger: destRef.current,
                            start: "top bottom",
                            end: "bottom top",
                            scrub: true,
                        }
                    }
                );
            }

            /* ─── LIVE & TESTIMONIALS reveals ─── */
            gsap.fromTo(liveRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
                    scrollTrigger: { trigger: liveRef.current, start: "top 80%", once: true }
                }
            );
            gsap.fromTo(testiRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 0.9, ease: "power3.out",
                    scrollTrigger: { trigger: testiRef.current, start: "top 82%", once: true }
                }
            );

            /* ─── CTA — big background text drifts horizontally ─── */
            gsap.fromTo(ctaRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1, y: 0, duration: 1, ease: "power3.out",
                    scrollTrigger: { trigger: ctaRef.current, start: "top 80%", once: true }
                }
            );
            if (ctaBgTextRef.current) {
                gsap.to(ctaBgTextRef.current, {
                    xPercent: -18,
                    ease: "none",
                    scrollTrigger: {
                        trigger: ctaRef.current,
                        start: "top bottom",
                        end: "bottom top",
                        scrub: 1.2,
                    },
                });
            }

        }, rootRef); // scope all animations to rootRef
        ScrollTrigger.refresh();
        return () => ctx.revert();
    }, []);

    /* ── Destination switcher ── */
    const switchDest = (idx) => {
        if (!destShowcaseRef.current || idx === activeDestIdx) return;
        gsap.to(destShowcaseRef.current, {
            opacity: 0, y: 14, duration: 0.22, ease: "power2.in",
            onComplete: () => {
                setActiveDestIdx(idx);
                gsap.to(destShowcaseRef.current, { opacity: 1, y: 0, duration: 0.38, ease: "power2.out" });
            },
        });
    };

    const d = DESTINATIONS[activeDestIdx];

    /* ════════════════════════════════════════ RENDER ════════════════════════════════════════ */
    return (
        <div className="hp" ref={rootRef}>

            {/* ═══════ HERO ═══════ */}
            <section className="hp-hero" ref={heroRef} id="hero">
                <div className="hp-hero-inner">

                    <div className="hp-morph-shape" ref={morphShapeRef} aria-hidden="true" />

                    <div className="hp-hero-copy">
                        <h1 className="hp-hero-h1">
                            <span className="line-mask"><span className="line-inner">Global Education</span></span>
                            <span className="line-mask"><span className="line-inner"><em>Reimagined.</em></span></span>
                        </h1>
                        <p className="hp-hero-sub" ref={heroSubRef}>
                            Your journey from India to the world starts with a single, seamless step.
                        </p>
                        <div ref={heroBtnRef}>
                            <Link to="/book-slot" className="hp-btn-primary">Start Journey</Link>
                        </div>
                    </div>

                    <div className="hp-hero-media">
                        <div className="hp-hero-card">
                            <img
                                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=900&auto=format&fit=crop"
                                alt="Students celebrating abroad"
                            />
                            {/* <div className="hp-float-pill hp-float-pill--tl">
                <span className="hp-pill-icon">🎓</span>
                <div><strong>1000+</strong><span>Placed</span></div>
              </div>
              <div className="hp-float-pill hp-float-pill--br">
                <span className="hp-pill-icon">✅</span>
                <div><strong>100%</strong><span>Visa Success</span></div>
              </div> */}
                        </div>
                    </div>
                </div>

                {/* <div className="hp-hero-scroll-hint">
          <span>Scroll to explore</span>
          <div className="hp-scroll-line" />
        </div> */}
            </section>

            {/* ═══════ STATS STRIP ═══════ */}
            <section className="hp-stats" ref={statsRef}>
                <div className="hp-stats-inner">
                    {[
                        { n: 1000, s: "+", l: "Students Placed" },
                        { n: 150, s: "+", l: "University Partners" },
                        { n: 15, s: "+", l: "Countries" },
                        { n: 10, s: "+", l: "Years Experience" },
                    ].map((item, i) => (
                        <div className="hp-stat-item" key={i}>
                            <span className="hp-stat-num"><AnimatedCounter target={item.n} suffix={item.s} /></span>
                            <span className="hp-stat-lbl">{item.l}</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* ═══════ ABOUT ═══════ */}
            <section className="hp-about" ref={aboutRef} id="about">
                <div className="hp-about-inner">

                    <div className="hp-about-media" ref={aboutMediaRef}>
                        <div className="hp-about-shape-land" aria-hidden="true" />
                        <div className="hp-about-img-main">
                            <img
                                ref={aboutImgRef}
                                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1000&auto=format&fit=crop"
                                alt="ISTP counselling session"
                            />
                        </div>
                        <div className="hp-about-img-sm">
                            <img
                                src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?q=80&w=600&auto=format&fit=crop"
                                alt="Happy students"
                            />
                        </div>
                        <div className="hp-exp-badge" ref={expBadgeRef}>
                            <span className="hp-exp-num">10+</span>
                            <span className="hp-exp-txt">Years of<br />Expertise</span>
                        </div>
                    </div>

                    <div className="hp-about-body" ref={aboutBodyRef}>
                        <span className="hp-eyebrow">Who We Are</span>
                        <h2 className="hp-section-h2">
                            India’s Trusted Gateway<br />
                            <em>to Global Universities</em>
                        </h2>
                        <p className="hp-about-para">
                            ISTP Overseas Education has been guiding students since 2014 — from choosing the right
                            university to landing with confidence in a new country. We combine deep institutional
                            knowledge with genuine care for each student's success. Proudly based in Vijayawada, Helping students succeed worldwide.
                        </p>
                        <ul className="hp-check-list">
                            {["Personalised one-on-one counselling", "End-to-end visa documentation",
                                "50+ global university partners", "Pre-departure orientation & support"].map((item, i) => (
                                    <li key={i}><span className="hp-check">✓</span>{item}</li>
                                ))}
                        </ul>
                        <Link to="/about" className="hp-link-arrow">Learn About ISTP →</Link>
                    </div>
                </div>
            </section>

            {/* ═══════ SERVICES — HORIZONTAL SCROLL ═══════ */}
            <section className="hp-services" ref={servicesRef} id="services">
                <div className="hp-svc-pin">

                    <div className="hp-svc-side">
                        <span className="hp-eyebrow hp-eyebrow--light">Our Services</span>
                        <h2 className="hp-section-h2 hp-section-h2--white">
                            Everything you<br />need, under<br /><em>one roof.</em>
                        </h2>
                        <p className="hp-svc-side-desc">
                            Six pillars of support — from your very first question to your final landing abroad.
                        </p>
                        <div className="hp-svc-progress">
                            <span className="hp-svc-progress-bar" ref={svcProgressRef} />
                        </div>
                        <span className="hp-svc-scroll-cue">Scroll →</span>
                    </div>

                    <div className="hp-svc-track" ref={svcTrackRef}>
                        {SERVICES.map((s, i) => (
                            <div
                                className="hp-svc-card"
                                key={i}
                                ref={(el) => (svcCardsRef.current[i] = el)}
                            >
                                <span className="hp-svc-num">0{i + 1}</span>
                                <span className="hp-svc-icon">{s.icon}</span>
                                <h3 className="hp-svc-title">{s.title}</h3>
                                <p className="hp-svc-desc">{s.desc}</p>
                                <Link to="/services" className="hp-svc-link">Learn more →</Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════ PROCESS — DRAWING LINE ═══════ */}
            <section className="hp-process" ref={processRef} id="process">
                <div className="hp-section-head hp-section-head--center">
                    <span className="hp-eyebrow hp-eyebrow--center">How It Works</span>
                    <h2 className="hp-section-h2 hp-section-h2--center">
                        From dream to destination<br />in <em>four simple steps.</em>
                    </h2>
                </div>
                <div className="hp-process-track">
                    <svg
                        className="hp-process-line-svg"
                        ref={procLineRef}
                        viewBox="0 0 1100 60"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                    >
                        <path d="M 120 28 L 980 28" />
                    </svg>
                    {PROCESS.map((p, i) => (
                        <div
                            className="hp-process-step"
                            key={i}
                            ref={(el) => (procStepsRef.current[i] = el)}
                        >
                            <div className="hp-process-num">{p.step}</div>
                            <h3 className="hp-process-title">{p.title}</h3>
                            <p className="hp-process-desc">{p.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ═══════ DESTINATIONS ═══════ */}
            <section className="hp-destinations" ref={destRef} id="destinations">
                <div className="hp-section-head hp-section-head--center">
                    <span className="hp-eyebrow hp-eyebrow--center">Study Destinations</span>
                    <h2 className="hp-section-h2 hp-section-h2--center">Where our students shine.</h2>
                </div>

                <div className="hp-dest-tabs">
                    {DESTINATIONS.map((dest, i) => (
                        <button
                            key={i}
                            className={`hp-dest-tab${activeDestIdx === i ? " hp-dest-tab--active" : ""}`}
                            onClick={() => switchDest(i)}
                        >
                            {dest.flag} {dest.name}
                        </button>
                    ))}
                </div>

                <div className="hp-dest-showcase" ref={destShowcaseRef}>
                    <div className="hp-dest-img-wrap" ref={destImgRef}>
                        <img src={d.img} alt={d.name} />
                        <div className="hp-dest-count-badge">
                            <span>{d.count}</span>
                            <span>Students</span>
                        </div>
                    </div>
                    <div className="hp-dest-info">
                        <h3 className="hp-dest-name">{d.flag} {d.name}</h3>
                        <p className="hp-dest-desc">
                            We've successfully placed <strong>{d.count} students</strong> in top universities
                            across {d.name}. Our expert counsellors have deep knowledge of the admission process,
                            visa requirements, and scholarship opportunities for Indian students.
                        </p>
                        <ul className="hp-dest-points">
                            {["Expert visa documentation support", "Scholarship & funding guidance",
                                "Pre-departure orientation", "Post-landing support network"].map((pt, i) => (
                                    <li key={i}><span className="hp-check">✓</span>{pt}</li>
                                ))}
                        </ul>
                        <Link to="/book-slot" className="hp-btn-primary">Book Free Consultation →</Link>
                    </div>
                </div>
            </section>

            {/* ═══════ LIVE SESSION ═══════ */}
            <section className="hp-live" ref={liveRef} id="live">
                <div className="hp-live-card">
                    <div className="hp-live-left">
                        <div className="hp-live-badge-row">
                            <span className="hp-live-dot" />
                            <span className="hp-live-label">LIVE DAILY</span>
                        </div>
                        <h2 className="hp-live-title">Join tonight's student meet.</h2>
                        <p className="hp-live-desc">
                            Ask real questions about universities, visas, and scholarships.
                            Every evening at <strong>7:00 PM IST</strong> — free, no sign-up required.
                        </p>
                        <Link to="/student-meets" className="hp-btn-primary">Join Live Session →</Link>
                    </div>
                    <div className="hp-live-right">
                        <p className="hp-cd-label">Next session starts in</p>
                        <Countdown />
                    </div>
                </div>
            </section>

            {/* ═══════ TESTIMONIALS ═══════ */}
            <section className="hp-testimonials" ref={testiRef} id="testimonials">
                <div className="hp-section-head hp-section-head--center">
                    <span className="hp-eyebrow hp-eyebrow--center">Student Stories</span>
                    <h2 className="hp-section-h2 hp-section-h2--center">
                        Heard directly from<br />our students.
                    </h2>
                </div>
                <TestimonialCarousel />
            </section>

            {/* ═══════ CTA ═══════ */}
            <section className="hp-cta" ref={ctaRef} id="cta">
                <div className="hp-cta-bg-text" ref={ctaBgTextRef} aria-hidden="true">GLOBAL</div>
                <div className="hp-cta-inner">
                    <h2 className="hp-cta-h2">Start your study abroad<br />journey <em>today.</em></h2>
                    <p className="hp-cta-sub">Book a free profile evaluation — no commitment, just clarity.</p>
                    <div className="hp-cta-actions">
                        <Link to="/book-slot" className="hp-btn-white">Book Free Counselling →</Link>
                        <a
                            href="https://wa.me/919110714332"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hp-btn-outline-white"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.878-1.42C8.34 21.47 10.13 22 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
                            </svg>
                            Chat on WhatsApp
                        </a>
                    </div>
                </div>
            </section>

            {/* WhatsApp float */}
            <div className="hp-wa-float">
                <span className="hp-wa-tip">Chat on WhatsApp</span>
                <a
                    href="https://wa.me/919110714332"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hp-wa-btn"
                    aria-label="Chat on WhatsApp"
                >
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                        <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.878-1.42C8.34 21.47 10.13 22 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2z" />
                    </svg>
                </a>
            </div>

        </div>
    );
}